import { cn } from "@u/cn";
import type { ReactNode } from "react";
import { FieldLabel } from "./FieldLabel";
import { LanguageTabs } from "./LanguageTabs";

/**
 * Canonical layout for a per-language field: a label row (label + language
 * picker) above the input(s) for the active language, passed as `children`.
 *
 * The inputs carry their own border/`bg-background` — a wrapping bordered card
 * here would frame the already-framed inputs and read as a "double margen" (two
 * nested boxes). So the group is held by the label + spacing only, no card.
 */
export function TranslatableField({
  label,
  languages,
  active,
  onChange,
  baseLang,
  missing,
  labelFor,
  className,
  children,
}: {
  /** Field label. A string is wrapped in `<FieldLabel>`; pass a node to override. */
  label?: ReactNode;
  languages: string[];
  active: string;
  onChange: (lang: string) => void;
  baseLang?: string;
  missing?: string[];
  labelFor?: (code: string) => string;
  className?: string;
  /** The input(s)/textarea(s) for the active language. */
  children: ReactNode;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2">
        {typeof label === "string" ? <FieldLabel>{label}</FieldLabel> : label}
        <LanguageTabs
          languages={languages}
          active={active}
          onChange={onChange}
          baseLang={baseLang}
          missing={missing}
          labelFor={labelFor}
          className="ml-auto"
        />
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
